import React, { useState, useEffect, useMemo, useRef } from "react";
import appLogo from "../../assets/image/CMS_LOGO.svg";
// JOB-3253 follow-up: Vite/ESM has no `require`; pre-import step images instead.
import stepOneImg from "../../assets/image/Step-one.png";
import signUp1Img from "../../assets/image/sign_up1.jpg";
import signupStep2DonorImg from "../../assets/image/signup-step-2-donor.png";
import signUp2Img from "../../assets/image/sign_up2.jpg";
import donorOneImg from "../../assets/image/donor-one.jpg";
import donorTwoImg from "../../assets/image/donor-two.jpg";
import jobStep2Img from "../../assets/image/job-step-2.png";
import { useFormik } from "formik";
import * as Yup from "yup";
import AxiosInstance from "../../utils/axiosInstance";
import { Grid, FormGroup, Typography, Autocomplete, Stepper, Step, StepLabel } from "@mui/material";
import InputDropdown from "../InputFields/InputDropdown";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckIcon from "@mui/icons-material/Check";
import { Link, useLocation, useNavigate } from "react-router-dom";
import InputText from "../InputFields/InputText";
import RenderParamsInputText from "../InputFields/RenderParamsInputText";
import { Row, Col } from "react-bootstrap";
import showToast from "../Toast/Toster";
import BlueButton from "../Button/BlueButton";
import { OrangeButton, WhiteLoaderComponent } from "../Icon/Index";
import { useUserType } from "../../contexts/UserTypeContext";
import DonorLogo from "../../assets/image/DONOR_LOGO.svg";
import "./style.css";
import WhiteButton from "../Button/WhiteButton";
import { getItem, setItem } from "../../utils/storage";

// SessionStorage helper functions
const saveStepData = (step, data) => {
  try {
    sessionStorage.setItem(`signupStep${step}Data`, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving step ${step} data:`, error);
  }
};

const getStepData = (step) => {
  try {
    const savedData = sessionStorage.getItem(`signupStep${step}Data`);
    if (savedData) {
      return JSON.parse(savedData);
    }
  } catch (error) {
    console.error(`Error getting step ${step} data:`, error);
  }
  return null;
};

const clearStepData = (step) => {
  try {
    sessionStorage.removeItem(`signupStep${step}Data`);
  } catch (error) {
    console.error(`Error clearing step ${step} data:`, error);
  }
};

const clearAllStepData = () => {
  try {
    sessionStorage.removeItem("signupStep0Data");
    sessionStorage.removeItem("signupStep1Data");
    sessionStorage.removeItem("signupStep2Data");
  } catch (error) {
    console.error("Error clearing all step data:", error);
  }
};

// Removed handleRefreshLogic - we don't want to clear data when navigating between steps
// Data should persist until the user completes signup or explicitly clears it

const Steps = ({ EmailAddress, Password, isDonationMode }) => {
  const { userType } = useUserType();
  const baseUrl = import.meta.env.VITE_BASE_API;
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);

  // Get active step number for progress bar (1, 2, or 3)
  // URL step mapping:
  // - step=null/1 → Progress Step 1 (Create Account)
  // - step=2 → Progress Step 2 (Activate) - currentStep=1 in Steps component
  // - step=3 → Progress Step 3 (Business Setup) - currentStep=2 in Steps component
  const activeProgressStep = useMemo(() => {
    const queryParams = new URLSearchParams(location?.search);
    const step = queryParams.get("step");
    let activeStep = 1;
    if (!step) {
      activeStep = 1; // SignUp form - Create Account
    } else if (step === "1") {
      activeStep = 1; // SignUp form - Create Account (shouldn't happen, but handle it)
    } else if (step === "2") {
      activeStep = 2; // Activate form - Step 2 active (Step 1 completed)
    } else if (step === "3") {
      activeStep = 3; // Business Setup form - Step 3 active (Steps 1 & 2 completed)
    }
    
    return activeStep;
  }, [location?.search, location]);
  const [industry, setIndustry] = useState([]);
  const [teamSize, setTeamSize] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [loader, setLoader] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);

  const getInitialCompanySearchInput = () => {
    const savedData = getStepData(1);
    if (savedData) {
      return savedData.CompanyName || "";
    }
    return "";
  };
  const [companySearchInput, setCompanySearchInput] = useState(getInitialCompanySearchInput());

  // Save company search input to session storage whenever it changes
  useEffect(() => {
    if (companySearchInput && currentStep === 1) {
      const savedData = getStepData(1) || {};
      saveStepData(1, {
        ...savedData,
        CompanyName: companySearchInput,
      });
    }
  }, [companySearchInput, currentStep]);

  const fetchData = async (currentStepa) => {
    if (currentStepa === 1) {
      try {
        const res = await AxiosInstance.get(`/v1/industry/dropdown`);
        if (res?.data?.statusCode === 200) {
          let industries = res?.data?.data?.map((item) => {
            return { label: item.Industry, value: item.IndustryId };
          });
          if (userType === "donor") {
            industries = industries.filter((industry) =>
              ["Non Profit", "Not For Profit"].includes(industry.label)
            );
          }

          setIndustry(industries);
        }
      } catch (error) {
        // Only surface a "server unreachable" toast on genuine network/5xx
        // failures. Auth-shaped errors (401/403) on the signup page just mean
        // the dropdown endpoint requires auth the unauthenticated user does
        // not yet have — nothing the user can do, no need for a scary toast.
        const status = error?.response?.status;
        const isAuthError = status === 401 || status === 403;
        if (!isAuthError) {
          showToast.error("Unable to connect to the server. Please try again later.");
        }
      }
    } else {
      // Commented out API calls - using static data instead
      // const res = await AxiosInstance.get(`/v1/revenue/dropdown`);
      // const res2 = await AxiosInstance.get(`/v1/teamsize/dropdown`);
      // if (res?.data?.statusCode === 200 && res2?.data?.statusCode === 200) {
      //   const data1 = res?.data?.data?.map((item) => {
      //     return { label: item.Revenue, value: item.RevenueId };
      //   });
      //   const data2 = res2?.data?.data?.map((item) => {
      //     return { label: item.TeamSize, value: item.TeamSizeId };
      //   });
      //   setRevenue(data1);
      //   setTeamSize(data2);
      // }
      
      // Static data for Revenue - value is same as label
      const staticRevenue = [
        { label: "Less than $100K", value: "Less than $100K" },
        { label: "$100K - $1M", value: "$100K - $1M" },
        { label: "$1M - $10M", value: "$1M - $10M" },
        { label: "$10M - $50M", value: "$10M - $50M" },
        { label: "$50M - $100M", value: "$50M - $100M" },
        { label: "$100M+", value: "$100M+" },
      ];
      
      // Static data for Team Size - value is same as label
      const staticTeamSize = [
        { label: "Just me / 1 employee", value: "Just me / 1 employee" },
        { label: "2-10 employees", value: "2-10 employees" },
        { label: "11-50 employees", value: "11-50 employees" },
        { label: "51-200 employees", value: "51-200 employees" },
        { label: "201-500 employees", value: "201-500 employees" },
        { label: "500+ employees", value: "500+ employees" },
      ];
      
      setRevenue(staticRevenue);
      setTeamSize(staticTeamSize);
    }
  };

  useEffect(() => {
    fetchData(1);

    const queryParams = new URLSearchParams(location?.search);
    const step = queryParams.get("step");
    
    // Restore CompanyName and companySearchInput on mount if on step 2
    if (step === "2") {
      const savedData = getStepData(1);
      if (savedData && savedData.CompanyName) {
        setCompanySearchInput(savedData.CompanyName);
      }
    }
  }, []);

  // Memoize initial values to prevent unnecessary re-renders
  const getInitialValues = useMemo(() => {
    const savedData = getStepData(1);
    if (savedData) {
      return {
        OwnerName: savedData.OwnerName || "",
        PhoneNumber: savedData.PhoneNumber || "",
        EmailAddress: savedData.EmailAddress || EmailAddress || "",
        Industry: savedData.Industry || null,
        CompanyName: savedData.CompanyName || "",

        TeamSize: savedData.TeamSize || null,
        Revenue: savedData.Revenue || null,
      };
    }
    return {
      OwnerName: "",
      PhoneNumber: "",
      EmailAddress: EmailAddress || "",
      Industry: null,
      CompanyName: "",
      TeamSize: null,
      Revenue: null,
    };
  }, [EmailAddress]);

  const formik = useFormik({
    initialValues: getInitialValues,
    enableReinitialize: false, // Changed to false to prevent reinitialization during typing
    validationSchema:
      currentStep === 1
        ? Yup.object({
          OwnerName: Yup.string().required("Full name is required"),
          PhoneNumber: Yup.string()
            .required("Phone number is required")
            .matches(
              /^\(\d{3}\) \d{3}-\d{4}$/,
              "Phone number must be in the format (xxx) xxx-xxxx"
            ),
          Industry: Yup.object().required("Industry is required"),
        })
        : Yup.object({
          CompanyName: Yup.string().required("Company name is required"),
          TeamSize: Yup.object().required("Team size is required"),
          Revenue: Yup.object().required("Revenue is required"),
        }),

    onSubmit: (values) => {
      if (currentStep === 1) {
        // Save step 1 data before moving to step 2
        saveStepData(1, {
          OwnerName: values.OwnerName || "",
          PhoneNumber: values.PhoneNumber || "",
          EmailAddress: values.EmailAddress || EmailAddress || "",
          Industry: values.Industry || null,
          CompanyName: values.CompanyName || "",
          TeamSize: values.TeamSize || null,
          Revenue: values.Revenue || null,
        });
        setCurrentStep(currentStep + 1);
        navigate(`/auth/signup?step=3`);
        fetchData(currentStep + 1);
      } else {
        // Additional validation before submission
        if (!values.TeamSize) {
          formik?.setFieldError("TeamSize", "Team size is required");
          formik?.setFieldTouched("TeamSize", true);
          return;
        }
        if (!values.Revenue) {
          formik?.setFieldError("Revenue", "Revenue is required");
          formik?.setFieldTouched("Revenue", true);
          return;
        }
        // Save step 2 data before final submission
        saveStepData(1, {
          OwnerName: values.OwnerName || "",
          PhoneNumber: values.PhoneNumber || "",
          EmailAddress: values.EmailAddress || EmailAddress || "",
          Industry: values.Industry || null,
          CompanyName: values.CompanyName || "",
          TeamSize: values.TeamSize || null,
          Revenue: values.Revenue || null,
        });
        handleSubmit(values);
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Auto-save form data whenever values change
  // Use a ref to track the latest values to avoid stale closures
  const valuesRef = useRef(formik.values);
  useEffect(() => {
    valuesRef.current = formik.values;
  }, [formik.values]);

  useEffect(() => {
    if (formik.values && (currentStep === 1 || currentStep === 2)) {
      // Debounce the save to avoid too many writes - increased to 1000ms for better performance
      const timeoutId = setTimeout(() => {
        // Use ref to get latest values to avoid stale closures
        const currentValues = valuesRef.current;
        saveStepData(1, {
          OwnerName: currentValues.OwnerName || "",
          PhoneNumber: currentValues.PhoneNumber || "",
          EmailAddress: currentValues.EmailAddress || EmailAddress || "",
          Industry: currentValues.Industry || null,
          CompanyName: currentValues.CompanyName || "",
          TeamSize: currentValues.TeamSize || null,
          Revenue: currentValues.Revenue || null,
        });
      }, 1000); // Save after 1000ms of no changes for better performance

      return () => clearTimeout(timeoutId);
    }
  }, [formik.values.OwnerName, formik.values.PhoneNumber, formik.values.EmailAddress, formik.values.Industry, formik.values.CompanyName, formik.values.TeamSize, formik.values.Revenue, currentStep, EmailAddress]);

  const formatPhoneNumber = (value) => {
    const PhoneNumber = value?.replace(/[^\d]/g, "");
    const limitedPhoneNumber = PhoneNumber.slice(0, 10);
    const match = limitedPhoneNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (match) {
      let formattedNumber = "";
      if (match[1].length >= 3) {
        formattedNumber = `(${match[1]}) `;
      } else {
        formattedNumber = match[1];
      }
      formattedNumber += match[2];
      if (match[3]) {
        formattedNumber += `-${match[3]}`;
      }
      return formattedNumber;
    }
    return limitedPhoneNumber;
  };

  const handlePhoneChange = (e) => {
    if (formik?.values?.PhoneNumber?.length > e?.target?.value?.length) {
      formik?.setFieldValue("PhoneNumber", e?.target?.value, false); // false = don't validate on change
    } else {
      const formattedValue = formatPhoneNumber(e?.target?.value);
      formik?.setFieldValue("PhoneNumber", formattedValue, false); // false = don't validate on change
    }
  };

  const handleSubmit = async (values) => {
    const step0Data = getStepData(0);
    const emailValue = EmailAddress || step0Data?.EmailAddress;
    const passwordValue = Password || step0Data?.Password;

    // Validate required values
    if (!emailValue || !passwordValue) {
      showToast.warning("Email and Password are required. Please go back to step 1.");
      return;
    }

    // Create a copy of values to avoid mutating formik's values object
    const submitValues = { ...values };
    
    submitValues.EmailAddress = emailValue;
    submitValues.Password = passwordValue;
    submitValues.IndustryId = submitValues?.Industry?.value;
    
    // Extract TeamSize value - handle both object and string formats
    if (submitValues.TeamSize) {
      if (typeof submitValues.TeamSize === 'object' && submitValues.TeamSize !== null) {
        // Extract the value (which is same as label now)
        submitValues.TeamSize = submitValues.TeamSize.value || submitValues.TeamSize.label || null;
      }
      // If it's already a string, keep it as is
    } else {
      submitValues.TeamSize = null;
    }
    
    // Extract Revenue value - handle both object and string formats
    if (submitValues.Revenue) {
      if (typeof submitValues.Revenue === 'object' && submitValues.Revenue !== null) {
        // Extract the value (which is same as label now)
        submitValues.Revenue = submitValues.Revenue.value || submitValues.Revenue.label || null;
      }
      // If it's already a string, keep it as is
    } else {
      submitValues.Revenue = null;
    }
    
    submitValues.Role = "Company";
    submitValues.IsDonationMode = userType === "donor";

    try {
      setLoader(true);

      const registerRes = await AxiosInstance.post(
        `/v1/auth/register`,
        submitValues
      );

      if (registerRes?.data?.statusCode === "200") {
        let isDonationPortal = false;
        if (!window.location.hostname.includes("clouddonormanager")) {
          isDonationPortal = false;
        } else {
          isDonationPortal = true;
        }

        const loginPayload = {
          EmailAddress: submitValues?.EmailAddress,
          Password: submitValues?.Password,
          CompanyId: registerRes?.data?.user?.CompanyId,
          UserId: registerRes?.data?.user?.UserId,
          isDonationPortal: isDonationPortal,
        };

        const loginRes = await AxiosInstance.post(
          `/v1/auth/login`,
          loginPayload
        );

        if (loginRes?.data?.statusCode === "200") {
          clearStepData(0);
          clearStepData(1);
          clearStepData(2);

          // Then set the new tokens
       
          setItem("adminToken", loginRes?.data?.token);
          setItem("CompanyId", loginRes?.data?.data?.UserId);
        

          showToast.success("Signed up and signed in successfully.", {
            autoClose: 3000,
          });

          const companyName = registerRes?.data?.userProfile?.CompanyUrl;
          if (!companyName) {
            console.error("CompanyName not found in response or form values");
            showToast.error("Error: Company name not available");
            navigate("/auth/login");
            return;
          }

          setTimeout(() => {
            navigate(`/${companyName}/index`, {
              state: { navigats: ["/index"] },
            });
          }, 1000);
        } else {
          const loginErrorMessage = Array.isArray(loginRes?.data?.message) 
            ? loginRes?.data?.message.join(", ")
            : (loginRes?.data?.message || "Login failed after registration");
          showToast.error(loginErrorMessage);
        }
      } else {
        // Handle non-200 status codes (including 400)
        const responseData = registerRes?.data || {};
        let registerErrorMessage = "Registration failed";
        
        if (responseData.message) {
          if (Array.isArray(responseData.message)) {
            registerErrorMessage = responseData.message.join(", ");
          } else if (typeof responseData.message === 'string') {
            registerErrorMessage = responseData.message;
          }
        } else if (responseData.error) {
          registerErrorMessage = typeof responseData.error === 'string' 
            ? responseData.error 
            : "Registration failed";
        }
        
        showToast.error(registerErrorMessage);
      }
    } catch (error) {
      console.error("Catch Error:", error);
      
      let errorMessage = "Unable to connect to the server. Please try again later.";
      
      // Check multiple possible error response structures
      if (error?.response?.data) {
        const responseData = error.response.data;
        
        // Handle message - could be string or array
        if (responseData.message) {
          if (Array.isArray(responseData.message)) {
            errorMessage = responseData.message.join(", ");
          } else if (typeof responseData.message === 'string') {
            errorMessage = responseData.message;
          }
        } else if (responseData.error) {
          if (typeof responseData.error === 'string') {
            errorMessage = responseData.error;
          } else if (Array.isArray(responseData.error)) {
            errorMessage = responseData.error.join(", ");
          }
        } else if (responseData.statusCode && responseData.statusCode !== 200) {
          // If statusCode exists but no message, use a default message
          errorMessage = "Registration failed. Please try again.";
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Ensure errorMessage is a valid string
      if (typeof errorMessage !== 'string' || errorMessage.trim() === '') {
        errorMessage = "Registration failed. Please try again.";
      }
      
      // Show error toast using system's standard toast
      showToast.error(errorMessage.trim());
    } finally {
      setLoader(false);
    }
  };

  // Restore Industry value after dropdown data is loaded for step 1
  useEffect(() => {
    if (currentStep === 1 && industry.length > 0 && formik?.values) {
      const savedData = getStepData(1);
      if (savedData && savedData.Industry) {
        const matchingIndustry = industry.find(
          (option) => option.value === savedData.Industry.value
        );
        if (matchingIndustry && (!formik.values.Industry || formik.values.Industry.value !== matchingIndustry.value)) {
          formik.setFieldValue("Industry", matchingIndustry);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry, currentStep]);

  // Restore Company Name when step 2 loads
  useEffect(() => {
    if (currentStep === 2 && formik?.values) {
      const savedData = getStepData(1);
      if (savedData && savedData.CompanyName) {
        const currentCompanyName = formik.values.CompanyName || "";
        if (currentCompanyName !== savedData.CompanyName) {
          formik.setFieldValue("CompanyName", savedData.CompanyName, false);
        }
        if (companySearchInput !== savedData.CompanyName) {
          setCompanySearchInput(savedData.CompanyName);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Restore TeamSize and Revenue values after dropdown data is loaded for step 2
  useEffect(() => {
    if (currentStep === 2 && teamSize.length > 0 && revenue.length > 0 && formik?.values) {
      const savedData = getStepData(1);
      if (savedData) {
        if (savedData.TeamSize) {
          let matchingTeamSize = null;
          // Handle both object (with value) and string formats
          // Since value is now same as label, we can find by either
          if (typeof savedData.TeamSize === 'object' && savedData.TeamSize.value) {
            // Find by value
            matchingTeamSize = teamSize.find(
              (option) => option.value === savedData.TeamSize.value
            );
          } else if (typeof savedData.TeamSize === 'string') {
            // Find by value or label (they are the same now)
            matchingTeamSize = teamSize.find(
              (option) => option.value === savedData.TeamSize || option.label === savedData.TeamSize
            );
          }
          
          if (matchingTeamSize && (!formik.values.TeamSize || formik.values.TeamSize?.value !== matchingTeamSize.value)) {
            formik.setFieldValue("TeamSize", matchingTeamSize);
          }
        }
        if (savedData.Revenue) {
          let matchingRevenue = null;
          // Handle both object (with value) and string formats
          // Since value is now same as label, we can find by either
          if (typeof savedData.Revenue === 'object' && savedData.Revenue.value) {
            // Find by value
            matchingRevenue = revenue.find(
              (option) => option.value === savedData.Revenue.value
            );
          } else if (typeof savedData.Revenue === 'string') {
            // Find by value or label (they are the same now)
            matchingRevenue = revenue.find(
              (option) => option.value === savedData.Revenue || option.label === savedData.Revenue
            );
          }
          
          if (matchingRevenue && (!formik.values.Revenue || formik.values.Revenue?.value !== matchingRevenue.value)) {
            formik.setFieldValue("Revenue", matchingRevenue);
          }
        }
      }
    }
    
  }, [teamSize, revenue, currentStep]);

  useEffect(() => {
    const adminToken = getItem("adminToken");
    if (adminToken) {
      // User is logged in, clean up signup data
      clearStepData(0);
      clearStepData(1);
      clearStepData(2);
    }
  }, []);

  useEffect(() => {
    setIsImageLoaded(false);
    const img = new Image();
    img.src =
      currentStep === 1
        ? userType === "donor"
          ? stepOneImg
          : signUp1Img
        : userType === "donor"
          ? signupStep2DonorImg
          : signUp2Img;
    img.onload = () => setIsImageLoaded(true);
  }, [currentStep]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location?.search);
    const step = queryParams.get("step");
    
    if (step === "2") {
      // When coming from SignUp form, show Activate (currentStep=1)
      // When user clicks next in Activate, form submission will set currentStep=2
      setCurrentStep(1);
      fetchData(1);
      
      // Restore data when navigating to step 1
      const savedData = getStepData(1);
      if (savedData) {
        formik.setValues({
          OwnerName: savedData.OwnerName || "",
          PhoneNumber: savedData.PhoneNumber || "",
          EmailAddress: savedData.EmailAddress || EmailAddress || "",
          Industry: savedData.Industry || null,
          CompanyName: savedData.CompanyName || "",
          TeamSize: savedData.TeamSize || null,
          Revenue: savedData.Revenue || null,
        });
        if (savedData.CompanyName) {
          setCompanySearchInput(savedData.CompanyName);
        }
      }
    } else if (step === "3") {
      // Step 3 means Business Setup (currentStep=2)
      setCurrentStep(2);
      fetchData(2);
      
      // Restore data when navigating to step 2
      const savedData = getStepData(1);
      if (savedData) {
        // Reset touched state and errors to prevent showing validation errors immediately
        formik.setTouched({}, false);
        formik.setErrors({});
        // Set values without triggering validation
        formik.setValues({
          OwnerName: savedData.OwnerName || "",
          PhoneNumber: savedData.PhoneNumber || "",
          EmailAddress: savedData.EmailAddress || EmailAddress || "",
          Industry: savedData.Industry || null,
          CompanyName: savedData.CompanyName || "",
          TeamSize: savedData.TeamSize || null,
          Revenue: savedData.Revenue || null,
        }, false); // false = don't validate on setValues
        if (savedData.CompanyName) {
          setCompanySearchInput(savedData.CompanyName);
        }
      }
    } else {
      setCurrentStep(1);
    }
  }, [location?.search]);

  const handleStepChange = (step) => {
    setCurrentStep(step);
    navigate(`/auth/signup?step=${step}`);
  };

  const handleBackStep = () => {
    if (currentStep > 1) {
      // Save current step data before going back
      if (currentStep === 2) {
        saveStepData(1, {
          OwnerName: formik.values.OwnerName || "",
          PhoneNumber: formik.values.PhoneNumber || "",
          EmailAddress: formik.values.EmailAddress || EmailAddress || "",
          Industry: formik.values.Industry || null,
          CompanyName: formik.values.CompanyName || "",
          TeamSize: formik.values.TeamSize || null,
          Revenue: formik.values.Revenue || null,
        });
      }
      
      // Going back from Business Setup (currentStep=2, step=3) to Activate (currentStep=1, step=2)
      const newStep = currentStep - 1;
      const savedData = getStepData(1);
      if (savedData) {
        // Restore form values
        formik.setValues({
          OwnerName: savedData.OwnerName || "",
          PhoneNumber: savedData.PhoneNumber || "",
          EmailAddress: savedData.EmailAddress || EmailAddress || "",
          Industry: savedData.Industry || null,
          CompanyName: savedData.CompanyName || "",
          TeamSize: savedData.TeamSize || null,
          Revenue: savedData.Revenue || null,
        });
        if (savedData.CompanyName) {
          setCompanySearchInput(savedData.CompanyName);
        }
      }
      setCurrentStep(newStep);
      navigate(`/auth/signup?step=${newStep === 1 ? 2 : 1}`);
    } else {
      // Going back from Activate to SignUp form
      navigate("/auth/signup");
    }
  };

  const handleBackToSignup = () => {
    navigate("/auth/signup");
  };

  const fetchCompanySuggestions = async (searchTerm) => {
    try {
      const response = await fetch(
        `https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(
          searchTerm
        )}`
      );
      const data = await response?.json();

      if (Array.isArray(data)) {
        const suggestions = data?.map((company) => ({
          CompanyName: company?.name,
          Domain: company?.domain,
          Logo: company?.logo,
        }));
        setCompanySuggestions(suggestions);
      }
    } catch (error) {
      console.error("Error fetching company suggestions:", error);
      const localSuggestions = [
        { CompanyName: "Acme Corporation" },
        { CompanyName: "Tech Solutions Inc" },
        { CompanyName: "Global Services Ltd" },
        { CompanyName: "Innovative Systems" },
        { CompanyName: "Digital Solutions" },
      ].filter((company) =>
        company.CompanyName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setCompanySuggestions(localSuggestions);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (companySearchInput && companySearchInput?.length >= 2) {
        fetchCompanySuggestions(companySearchInput);
      } else {
        setCompanySuggestions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companySearchInput]);

  return (
    <>
      <Grid className="w-100 d-flex steps-container" >
        <Grid
          className="steps-form-container"
          size={{
            xs: 12,
            md: 7
          }}>
          <Grid className="steps d-flex align-items-center justify-content-center" style={{ width: "100%", maxWidth: "100%" }}>
            <Grid className="my-steps d-flex align-items-center justify-content-center" style={{ width: "100%", maxWidth: "75%" }}>
              <Grid className="app-logos mb-4 d-flex justify-content-center align-items-center" style={{ width: "100%"}}>
                <Link to="/auth/login" onClick={() => clearAllStepData()}>
                  {userType === "donor" ? (
                    <img
                      src={DonorLogo}
                      alt="logo"
                      className="signup-logo-img"
                      style={{ width: "330px" }}
                    />
                  ) : (
                    <img
                      src={appLogo}
                      alt="logo"
                      className="signup-logo-img"
                      style={{ width: "330px" }}
                    />
                  )}
                </Link>
              </Grid>

              {/* Progress Bar - MUI Stepper */}
              <Stepper
                activeStep={activeProgressStep - 1}
                alternativeLabel
                sx={{
                  width: '100%',
                  marginBottom: '10px',
                  marginTop: '10px',
                  '& .MuiStepLabel-root .Mui-completed': {
                    color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                  },
                  '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel': {
                    color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                    fontWeight: 500,
                  },
                  '& .MuiStepLabel-root .Mui-active': {
                    color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                  },
                  '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel': {
                    color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                    fontWeight: 500,
                  },
                  '& .MuiStepLabel-root .Mui-disabled': {
                    color: 'rgba(153, 153, 153, 1)',
                  },
                  '& .MuiStepLabel-label.Mui-disabled.MuiStepLabel-alternativeLabel': {
                    color: 'rgba(153, 153, 153, 1)',
                  },
                  '& .MuiStepConnector-root': {
                    top: '20px',
                    left: 'calc(-50% + 20px)',
                    right: 'calc(50% + 20px)',
                  },
                  '& .MuiStepConnector-line': {
                    borderTopWidth: '2px',
                    borderColor: isDonationMode ? 'rgba(104, 178, 203, 0.2)' : 'rgba(212, 148, 58, 0.2)',
                  },
                  '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                    borderColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                  },
                  '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                    borderColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                  },
                  '& .MuiStepIcon-root': {
                    color: isDonationMode ? 'rgba(104, 178, 203, 0.2)' : 'rgba(212, 148, 58, 0.2)',
                    fontSize: '2rem',
                    border: `2px solid ${isDonationMode ? 'rgba(104, 178, 203, 0.2)' : 'rgba(212, 148, 58, 0.2)'}`,
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    '&.Mui-active': {
                      color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                      border: `2px solid ${isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)'}`,
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    },
                    '&.Mui-completed': {
                      color: 'rgba(255, 255, 255, 1)',
                      border: '2px solid rgba(255, 255, 255, 1)',
                      backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                    },
                  },
                  '& .MuiStepIcon-text': {
                    fill: 'currentColor',
                    fontSize: '16px',
                    fontWeight: 600,
                    fontFamily: '"Poppins", sans-serif',
                  },
                  '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
                    fill: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                  },
                  '& .MuiStepIcon-root.Mui-completed .MuiStepIcon-text': {
                    fill: 'rgba(255, 255, 255, 1)',
                  },
                  '& .MuiStepLabel-label': {
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    marginTop: '10px',
                  },
                }}
              >
                <Step>
                  <StepLabel
                    StepIconComponent={(props) => {
                      if (props.completed) {
                        return (
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                              border: '2px solid rgba(255, 255, 255, 1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckIcon style={{ color: 'rgba(255, 255, 255, 1)', fontSize: '20px' }} />
                          </div>
                        );
                      }
                      return (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: props.active
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(255, 255, 255, 1)',
                            border: `2px solid ${
                              props.active
                                ? isDonationMode
                                  ? 'rgba(104, 178, 203, 1)'
                                  : 'rgba(212, 148, 58, 1)'
                                : isDonationMode
                                ? 'rgba(104, 178, 203, 0.2)'
                                : 'rgba(212, 148, 58, 0.2)'
                            }`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color:
                              props.active
                                ? isDonationMode
                                  ? 'rgba(104, 178, 203, 1)'
                                  : 'rgba(212, 148, 58, 1)'
                                : isDonationMode
                                ? 'rgba(104, 178, 203, 0.3)'
                                : 'rgba(212, 148, 58, 0.3)',
                            fontSize: '16px',
                            fontWeight: 600,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          {props.icon}
                        </div>
                      );
                    }}
                  >
                    Create Account
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel
                    StepIconComponent={(props) => {
                      if (props.completed) {
                        return (
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                              border: '2px solid rgba(255, 255, 255, 1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckIcon style={{ color: 'rgba(255, 255, 255, 1)', fontSize: '20px' }} />
                          </div>
                        );
                      }
                      return (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            border: `2px solid ${
                              props.active
                                ? isDonationMode
                                  ? 'rgba(104, 178, 203, 1)'
                                  : 'rgba(212, 148, 58, 1)'
                                : isDonationMode
                                ? 'rgba(104, 178, 203, 0.2)'
                                : 'rgba(212, 148, 58, 0.2)'
                            }`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color:
                              props.active
                                ? isDonationMode
                                  ? 'rgba(104, 178, 203, 1)'
                                  : 'rgba(212, 148, 58, 1)'
                                : isDonationMode
                                ? 'rgba(104, 178, 203, 0.3)'
                                : 'rgba(212, 148, 58, 0.3)',
                            fontSize: '16px',
                            fontWeight: 600,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          {props.icon}
                        </div>
                      );
                    }}
                  >
                    Activate
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel
                    StepIconComponent={(props) => {
                      if (props.completed) {
                        return (
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                              border: '2px solid rgba(255, 255, 255, 1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckIcon style={{ color: 'rgba(255, 255, 255, 1)', fontSize: '20px' }} />
                          </div>
                        );
                      }
                      return (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            border: `2px solid ${
                              props.active
                                ? isDonationMode
                                  ? 'rgba(104, 178, 203, 1)'
                                  : 'rgba(212, 148, 58, 1)'
                                : isDonationMode
                                ? 'rgba(104, 178, 203, 0.2)'
                                : 'rgba(212, 148, 58, 0.2)'
                            }`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color:
                              props.active
                                ? isDonationMode
                                  ? 'rgba(104, 178, 203, 1)'
                                  : 'rgba(212, 148, 58, 1)'
                                : isDonationMode
                                ? 'rgba(104, 178, 203, 0.3)'
                                : 'rgba(212, 148, 58, 0.3)',
                            fontSize: '16px',
                            fontWeight: 600,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          {props.icon}
                        </div>
                      );
                    }}
                  >
                    Business Setup
                  </StepLabel>
                </Step>
              </Stepper>

              <Grid
                className="freetrialHeadFont step-heading"
              >
                {currentStep === 1 && "Your Free Trial Is Now Active!"}
                {currentStep === 2 && "Set Up Your Business!"}
              </Grid>

              <form id="multi-step-form" onSubmit={formik.handleSubmit}>
                {currentStep === 1 && (
                  <Grid className="step step-1">
                    <Typography
                      className="heading-four step-subheading"
                    >
                      Let's get you started.
                    </Typography>
                    <FormGroup>
                      <FormGroup
                        style={{ width: "100%", marginTop: "8px" }}
                        className="fullnemae fullname-step"
                      >
                        {/* <label className="signup-form-label">Full name</label> */}

                        <InputText
                          value={formik?.values?.OwnerName || ""}
                          onChange={(e) => {
                            formik.setFieldValue("OwnerName", e.target.value, false); // false = don't validate on change
                          }}
                          onBlur={formik.handleBlur}
                          error={
                            formik?.touched?.OwnerName &&
                            Boolean(formik?.errors?.OwnerName)
                          }
                          helperText={
                            formik?.touched?.OwnerName &&
                            formik?.errors?.OwnerName
                          }
                          name="OwnerName"
                          placeholder="Enter full name"
                          label="Full name"
                          required
                          type="text"
                          className="text-blue-color w-100"
                          fieldHeight="44px"
                        />
                      </FormGroup>
                      <FormGroup
                        style={{ width: "100%", marginTop: "16px" }}
                        className="fullnemae phonenumber-signup"
                      >
                        {/* <label className="signup-form-label">Phone number</label> */}
                        <InputText
                          value={formik?.values?.PhoneNumber}
                          onChange={handlePhoneChange}
                          onBlur={formik?.handleBlur}
                          error={
                            formik?.touched?.PhoneNumber &&
                            Boolean(formik?.errors?.PhoneNumber)
                          }
                          helperText={
                            formik?.touched?.PhoneNumber &&
                            formik?.errors?.PhoneNumber
                          }
                          name="PhoneNumber"
                          placeholder="(555) 555-1234"
                          label="Phone number"
                          required
                          type="text"
                          className="text-blue-color w-100 trialPhoneNumberInput"
                          fieldHeight="44px"
                        />
                      </FormGroup>
                      <Grid className="Drop" style={{ marginTop: "16px", width: "100%" }}>
                        <InputDropdown
                          label="Industry type"
                          required
                          name="Industry"
                          options={(industry || [])
                            .slice()
                            .sort((a, b) =>
                              (a.label || "").localeCompare(b.label || "", "en", {
                                sensitivity: "base",
                              })
                            )}
                          placeholder="Choose industry type..."
                          value={formik?.values?.Industry}
                          onChange={(_, selectedOption) => {
                            formik?.setFieldValue("Industry", selectedOption);
                            if (selectedOption) {
                              formik?.setFieldError("Industry", undefined);
                            } else {
                              formik?.setFieldError(
                                "Industry",
                                "Industry is Required"
                              );
                            }
                          }}
                          onBlur={() => {
                            formik?.setFieldTouched("Industry", true);
                            if (!formik?.values?.Industry) {
                              formik?.setFieldError(
                                "Industry",
                                "Industry is Required"
                              );
                            }
                          }}
                          error={
                            formik?.touched?.Industry &&
                            Boolean(formik?.errors?.Industry)
                          }
                          helperText={
                            formik?.touched?.Industry &&
                            formik?.errors?.Industry
                              ? typeof formik.errors.Industry === "string"
                                ? formik.errors.Industry
                                : "Industry is required"
                              : undefined
                          }
                          disableClearable={!formik?.values?.Industry}
                          textFieldProps={{ className: "text-blue-color w-100" }}
                          fieldHeight="44px"
                        />
                      </Grid>
                    </FormGroup>
                    <div className="step-buttons-container">
                      <WhiteButton
                        type="button"
                        onClick={handleBackToSignup}
                        isDonationMode={isDonationMode}
                        label={"Previous Step"}
                        className="step-button step-button-white"
                      />
                      <OrangeButton
                        type="button"
                        onClick={() => {
                          formik?.handleSubmit();
                        }}
                        isDonationMode={isDonationMode}
                        label={"Next Step"}
                        className="step-button step-button-orange"
                      />
                    </div>
                  </Grid>
                )}

                {currentStep === 2 && (
                  <Grid className="step step-1">
                    <Typography
                      className="heading-five step-subheading step-2-subheading"
                    >
                      We'll use this information to personalize your experience.
                    </Typography>
                    <FormGroup>
                      <FormGroup
                        style={{ width: "100%", marginTop: "8px" }}
                        className="fullnemae"
                      >
                      {/* <label className="signup-form-label">Company name</label> */}

                        <Autocomplete
                          freeSolo
                          options={companySuggestions}
                          getOptionLabel={(option) =>
                            typeof option === "string"
                              ? option
                              : option.CompanyName || ""
                          }
                          inputValue={companySearchInput || formik?.values?.CompanyName || ""}
                          value={formik?.values?.CompanyName || ""}
                          onInputChange={(_, newInputValue) => {
                            setCompanySearchInput(newInputValue);
                            formik?.setFieldValue("CompanyName", newInputValue, false); // false = don't validate on change
                            // Save immediately when company name changes
                            const currentData = getStepData(1) || {};
                            saveStepData(1, {
                              ...currentData,
                              CompanyName: newInputValue,
                            });
                          }}
                          onChange={(_, newValue) => {
                            if (newValue && typeof newValue === "object") {
                              const companyName = newValue.CompanyName;
                              formik?.setFieldValue("CompanyName", companyName);
                              setCompanySearchInput(companyName);
                              // Save immediately when company is selected
                              const currentData = getStepData(1) || {};
                              saveStepData(1, {
                                ...currentData,
                                CompanyName: companyName,
                              });
                            } else if (typeof newValue === "string") {
                              formik?.setFieldValue("CompanyName", newValue);
                              setCompanySearchInput(newValue);
                              // Save immediately
                              const currentData = getStepData(1) || {};
                              saveStepData(1, {
                                ...currentData,
                                CompanyName: newValue,
                              });
                            } else if (newValue === null) {
                              formik?.setFieldValue("CompanyName", "");
                              setCompanySearchInput("");
                              // Save immediately
                              const currentData = getStepData(1) || {};
                              saveStepData(1, {
                                ...currentData,
                                CompanyName: "",
                              });
                            }
                          }}
                          renderInput={(params) => (
                            <RenderParamsInputText
                              params={params}
                              value={formik?.values?.CompanyName || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                formik?.setFieldValue("CompanyName", value);
                                setCompanySearchInput(value);
                                // Save immediately when typing
                                const currentData = getStepData(1) || {};
                                saveStepData(1, {
                                  ...currentData,
                                  CompanyName: value,
                                });
                              }}
                              onBlur={formik?.handleBlur}
                              error={
                                formik?.touched?.CompanyName &&
                                Boolean(formik?.errors?.CompanyName)
                              }
                              helperText={
                                formik?.touched?.CompanyName &&
                                formik?.errors?.CompanyName
                              }
                              name="CompanyName"
                              placeholder="Enter company name"
                              label="Company name"
                              required
                              type="text"
                              className="text-blue-color w-100"
                              fieldHeight="44px"
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {option.Logo && (
                                  <img
                                    src={option.Logo}
                                    alt={option.CompanyName}
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      marginRight: "8px",
                                      objectFit: "contain",
                                    }}
                                  />
                                )}
                                <div>
                                  <div style={{ fontWeight: "bold" }}>
                                    {option.CompanyName}
                                  </div>
                                  {option.Domain && (
                                    <div
                                      style={{
                                        fontSize: "0.8em",
                                        color: "#666",
                                      }}
                                    >
                                      {option.Domain}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </li>
                          )}
                          filterOptions={(options, state) => {
                            return options.filter((option) => {
                              const companyName = option?.CompanyName || "";
                              return companyName
                                .toLowerCase()
                                .includes(state.inputValue.toLowerCase());
                            });
                          }}
                          noOptionsText="No matching companies found"
                        />
                      </FormGroup>
                      <Grid style={{ marginTop: "16px", width: "100%" }}>
                        <InputDropdown
                          label="Team size"
                          required
                          name="TeamSize"
                          options={teamSize}
                          placeholder="Select your team size (including yourself)"
                          value={formik?.values?.TeamSize}
                          onChange={(_, selectedOption) => {
                            formik?.setFieldValue("TeamSize", selectedOption);
                            if (selectedOption) {
                              formik?.setFieldError("TeamSize", undefined);
                            } else {
                              formik?.setFieldError(
                                "TeamSize",
                                "Team size is required"
                              );
                            }
                            const currentData = getStepData(1) || {};
                            saveStepData(1, {
                              ...currentData,
                              TeamSize: selectedOption,
                            });
                          }}
                          onBlur={() => {
                            formik?.setFieldTouched("TeamSize", true);
                            if (!formik?.values?.TeamSize) {
                              formik?.setFieldError(
                                "TeamSize",
                                "Team size is required"
                              );
                            }
                          }}
                          error={
                            formik?.touched?.TeamSize &&
                            Boolean(formik?.errors?.TeamSize)
                          }
                          helperText={
                            formik?.touched?.TeamSize &&
                            formik?.errors?.TeamSize
                              ? typeof formik.errors.TeamSize === "string"
                                ? formik.errors.TeamSize
                                : "Team size is required"
                              : undefined
                          }
                          disableClearable={!formik?.values?.TeamSize}
                          textFieldProps={{ className: "w-100" }}
                          fieldHeight="44px"
                        />
                      </Grid>
                      <Grid style={{ marginTop: "16px", width: "100%" }}>
                        <InputDropdown
                          label="Annual revenue"
                          required
                          name="Revenue"
                          options={revenue}
                          placeholder="Select your estimated annual revenue"
                          value={formik?.values?.Revenue}
                          onChange={(_, selectedOption) => {
                            formik?.setFieldValue("Revenue", selectedOption);
                            if (selectedOption) {
                              formik?.setFieldError("Revenue", undefined);
                            } else {
                              formik?.setFieldError(
                                "Revenue",
                                "Revenue is required"
                              );
                            }
                            const currentData = getStepData(1) || {};
                            saveStepData(1, {
                              ...currentData,
                              Revenue: selectedOption,
                            });
                          }}
                          onBlur={() => {
                            formik?.setFieldTouched("Revenue", true);
                            if (!formik?.values?.Revenue) {
                              formik?.setFieldError(
                                "Revenue",
                                "Revenue is required"
                              );
                            }
                          }}
                          error={
                            formik?.touched?.Revenue &&
                            Boolean(formik?.errors?.Revenue)
                          }
                          helperText={
                            formik?.touched?.Revenue &&
                            formik?.errors?.Revenue
                              ? typeof formik.errors.Revenue === "string"
                                ? formik.errors.Revenue
                                : "Revenue is required"
                              : undefined
                          }
                          disableClearable={!formik?.values?.Revenue}
                          textFieldProps={{
                            className: "basic-single secondstrpMarginTOP w-100",
                          }}
                          fieldHeight="44px"
                        />
                      </Grid>
                    </FormGroup>
                    <div className="step-buttons-container">
                      <WhiteButton
                        type="button"
                        onClick={handleBackStep}
                        isDonationMode={isDonationMode}
                        label={"Previous Step"}
                        className="step-button step-button-white"
                      />
                      <OrangeButton
                        type="button"
                        onClick={() => {
                          formik?.handleSubmit();
                        }}
                        isDonationMode={isDonationMode}
                        disabled={loader}
                        className="step-button step-button-orange"
                        label={
                          loader ? (
                            <WhiteLoaderComponent
                              height="20"
                              width="20"
                              padding="20"
                              loader={loader}
                            />
                          ) : (
                            "Start"
                          )
                        }
                      />
                    </div>
                  </Grid>
                )}
              </form>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          className="main-imageeee img signup-side-image"
          size={{
            xs: 12,
            md: 5
          }}>
          <img
            src={
              currentStep === 1
                ? userType === "donor"
                  ? donorOneImg
                  : stepOneImg
                : userType === "donor"
                  ? donorTwoImg
                  : jobStep2Img
            }
            alt="Signup"
            className="signup-side-image-img"
            style={{
              width: "100%",
              height: "100vh",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default Steps;
